import { z } from 'zod'

import { nullish, paginated, timestamp, ulid } from '@/shared/api'

/**
 * The in-app inbox — backend-routes.md §18, in the shape core's
 * `feat/notifications-complete` (core PR #24) serves.
 *
 * **Core renders every word.** `title_fa` and `body_fa` arrive finished, in
 * Persian, with amounts in Toman and Persian digits, and `tone` arrives
 * decided by the template. The client formats `created_at` and nothing else;
 * it never builds or rewords a message.
 *
 * Every key is present on every item, `deeplink` and `created_at` as an
 * explicit `null` when there is none, so both are read through `nullish()`.
 */

/**
 * What the message is about. Read but not drawn: the frame colours a row by
 * `tone`, not by category. A category core adds later reads as `system`
 * rather than failing the whole page.
 */
export const notificationCategory = z
  .enum(['transaction', 'price_alert', 'kyc', 'security', 'system'])
  .catch('system')
export type NotificationCategory = z.infer<typeof notificationCategory>

/**
 * The icon's colour: green, primary blue or red. Core falls back to `info`
 * for a row whose template it no longer knows, and so does this.
 */
export const notificationTone = z.enum(['success', 'info', 'danger']).catch('info')
export type NotificationTone = z.infer<typeof notificationTone>

export const notificationItem = z.object({
  id: ulid,
  category: notificationCategory,
  tone: notificationTone,
  /** Empty for a row whose template core no longer knows. */
  title_fa: z.string(),
  body_fa: z.string(),
  /** `saminex://…`; `null` when the message opens nothing. See `lib/deeplink.ts`. */
  deeplink: nullish(z.string()),
  is_read: z.boolean(),
  /** ISO UTC with microseconds and `Z`. */
  created_at: nullish(timestamp),
})

export type NotificationItem = z.infer<typeof notificationItem>

/**
 * `GET /notifications`: §1's page, newest first. `next_cursor` is the last
 * item's id while there may be more, and `null` on the last page.
 */
export const notificationsPage = paginated(notificationItem)

export type NotificationsPage = z.infer<typeof notificationsPage>

/** `GET /notifications/unread-count`: in-app unread rows only. */
export const unreadCountResponse = z.object({
  unread: z.number().int().nonnegative(),
})
